/**
 * iter240 — Fil de discussion client ↔ garage, côté client (pages publiques).
 * Utilisé sur le portail de suivi (par dossier) et sur la page d'approbation du devis.
 * Texte + photo (compressée), lecture seule côté historique.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, MessageSquare, Send, X } from "lucide-react";
import { photoCompress } from "@/components/or/photoCompress";
import { traducteur } from "@/i18n/suivi";

const fmt = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-CH", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

/**
 * @param baseUrl   URL des messages, ex. `${API}/${token}/or/${orId}/messages`
 * @param photoUrl  fonction (messageId) => URL de la photo
 * @param accent    couleur d'accent du garage
 * @param garageNom nom affiché pour les réponses du garage
 */
export default function PublicMessages({ baseUrl, photoUrl, accent = "#52a675", garageNom = "Garage", autoOpen = false, badge = 0, testId = "messages", lang = "fr" }) {
  const t = traducteur(lang);   // iter274 — FR / DE
  const [open, setOpen] = useState(autoOpen);
  const [items, setItems] = useState([]);
  const [texte, setTexte] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fin = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(baseUrl);
      setItems(data.items || []);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, [baseUrl]);

  useEffect(() => { if (open && !loaded) load(); }, [open, loaded, load]);
  useEffect(() => { if (open) fin.current?.scrollIntoView({ block: "nearest" }); }, [items, open]);

  const pickPhoto = async (files) => {
    const f = files?.[0];
    if (!f) return;
    try {
      const dataUrl = await photoCompress(f);
      setPhoto(dataUrl);
    } catch {
      toast.error(t("image_illisible"));
    }
  };

  const send = async () => {
    if (!texte.trim() && !photo) return;
    setBusy(true);
    try {
      const { data } = await axios.post(baseUrl, { texte: texte.trim(), photo });
      setItems((prev) => [...prev, data]);
      setTexte("");
      setPhoto(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("envoi_ko"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
        data-testid={`${testId}-open`}
      >
        <MessageSquare className="w-3.5 h-3.5" style={{ color: accent }} />
        {t("ecrire_garage")}
        {badge > 0 && (
          <span className="ml-1 px-1.5 rounded-full bg-red-500 text-white text-[10px]">{badge}</span>
        )}
      </button>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`${testId}-panel`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <MessageSquare className="w-3.5 h-3.5" /> {t("discussion")}
        </div>
        <button type="button" onClick={() => setOpen(false)} data-testid={`${testId}-close`}>
          <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
        </button>
      </div>

      <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid={`${testId}-list`}>
        {!loaded ? (
          <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
        ) : items.length === 0 ? (
          <p className="text-[11px] text-gray-400 py-2">
            {t("aucun_message")}
          </p>
        ) : (
          items.map((m) => {
            const mine = m.auteur === "client";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`${testId}-item`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? "text-white" : "bg-gray-100 text-gray-800"}`}
                  style={mine ? { backgroundColor: accent } : undefined}
                >
                  {!mine && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{garageNom}</div>}
                  {m.texte && <div className="whitespace-pre-wrap break-words">{m.texte}</div>}
                  {m.a_photo && (
                    <a href={photoUrl(m.id)} target="_blank" rel="noreferrer">
                      <img
                        src={photoUrl(m.id)}
                        alt={t("piece_jointe")}
                        className="mt-1 rounded-lg max-h-40 object-cover"
                        data-testid={`${testId}-photo-${m.id}`}
                      />
                    </a>
                  )}
                  <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-gray-400"}`}>{fmt(m.created_at)}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={fin} />
      </div>

      {photo && (
        <div className="mt-2 relative inline-block">
          <img src={photo} alt={t("a_envoyer")} className="h-16 rounded-lg border border-gray-200" />
          <button
            type="button"
            onClick={() => setPhoto(null)}
            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
            data-testid={`${testId}-photo-remove`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2 mt-2">
        <label className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer flex-shrink-0">
          <Camera className="w-4 h-4" />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => pickPhoto(e.target.files)}
            data-testid={`${testId}-photo-input`}
          />
        </label>
        <textarea
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder={t("votre_message")}
          rows={2}
          className="flex-1 min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-gray-300"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={send}
          disabled={busy || (!texte.trim() && !photo)}
          className="p-2.5 rounded-lg text-white disabled:opacity-40 flex-shrink-0"
          style={{ backgroundColor: accent }}
          data-testid={`${testId}-send`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
